/**
 * Custo de uma cotação. O DIFAL é informado em % e incide
 * sobre o custo dos produtos (quantidade × preço unitário). O custo unitário
 * final dilui setup, frete, impostos e outros custos na quantidade cotada.
 * Mesma regra do backend (SupplierQuoteService.resolveTaxCents).
 */
export interface QuoteCostInput {
    quantity: number
    unitPriceCents: number
    setupFeeCents?: number
    shippingFeeCents?: number
    otherCostsCents?: number
    taxPercent?: number | null
}

export interface QuoteCost {
    subtotalCents: number
    taxCents: number
    additionalCents: number
    totalCents: number
    unitCostCents: number | null
}

const safe = (value?: number | null) => (Number.isFinite(value) && (value as number) > 0 ? (value as number) : 0)

export const computeQuoteCost = (input: QuoteCostInput): QuoteCost => {
    const quantity = safe(input.quantity)
    const subtotalCents = Math.round(quantity * safe(input.unitPriceCents))
    const taxCents = Math.round((subtotalCents * safe(input.taxPercent)) / 100)
    const additionalCents = safe(input.setupFeeCents) + safe(input.shippingFeeCents) + safe(input.otherCostsCents)
    const totalCents = subtotalCents + taxCents + additionalCents
    return {
        subtotalCents,
        taxCents,
        additionalCents,
        totalCents,
        unitCostCents: quantity > 0 ? Math.round(totalCents / quantity) : null,
    }
}

/** "6", "6,5" ou "6.5" → 6.5; vazio → null. */
export const parsePercent = (input?: string | null): number | null => {
    if (input == null || input.trim() === '') return null
    const value = Number(input.trim().replace('%', '').replace(',', '.'))
    return Number.isFinite(value) ? value : null
}

/** Custo unitário com tudo diluído; usa o total salvo na cotação. */
export const unitCostCents = (totalCents?: number | null, quantity?: number | null) =>
    totalCents != null && quantity ? Math.round(totalCents / quantity) : null
